import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, ShieldCheck, UserCog, Sparkles, BellRing, MessageCircle, Mail, Trash2, AlertTriangle } from 'lucide-react';
import { apiFetch, clearTokens, ApiClientError } from '@/lib/api';
import { startLineOauthLink, type LineLinkMe } from '@/lib/line-link';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';
import { formatUserName } from '@/lib/format';
import { PasswordSetInput, isPasswordSetValid } from '@/components/PasswordSetInput';
import { SUBSCRIPTION_EVENT_LABELS } from '@/lib/notification-labels';
import { ConfirmDialog } from '@/components/Modal';
import { ToggleSwitch } from '@/components/ToggleSwitch';

type AiUsageMe = {
  month: string;
  costUsd: number;
  tokens: number;
  requestCount: number;
  appliedLimitUsd: number | null;
};

type NotificationSubscription = { event_type_id: string; label_key: string; category: string; is_subscribed: boolean };

/** v0.4 §7 通知基盤: 個人の通知購読ON/OFF (opt-out方式)。`/settings/notifications` タブは
 *  notification:manage 限定のため、一般スタッフでも必ず到達できる `/profile` 側に置く。 */
function NotificationSubscriptionsSection() {
  const qc = useQueryClient();
  const { data: subscriptions = [], isLoading } = useQuery<NotificationSubscription[]>({
    queryKey: ['notification-subscriptions'],
    queryFn: () => apiFetch<{ subscriptions: NotificationSubscription[] }>('/me/notification-subscriptions').then((r) => r.subscriptions),
  });

  const toggleMut = useMutation({
    mutationFn: (v: { event_type_id: string; is_subscribed: boolean }) =>
      apiFetch('/me/notification-subscriptions', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-subscriptions'] }),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  if (!isLoading && subscriptions.length === 0) return null;

  return (
    <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <BellRing size={16} className="text-chrome-base" />
        <h2 className="text-h3">通知の受け取り設定</h2>
      </div>
      <p className="text-xs text-neutral-sub mb-2">オフにした通知は届かなくなります (それ以外の通知は既定で受け取ります)</p>
      {isLoading ? (
        <div className="text-neutral-sub text-sm">読み込み中…</div>
      ) : (
        <div className="divide-y divide-neutral-line">
          {subscriptions.map((s) => (
            <div key={s.event_type_id} className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-sm text-neutral-text">{SUBSCRIPTION_EVENT_LABELS[s.event_type_id] ?? s.event_type_id}</span>
              <ToggleSwitch
                checked={s.is_subscribed}
                disabled={toggleMut.isPending}
                onChange={(v) => toggleMut.mutate({ event_type_id: s.event_type_id, is_subscribed: v })}
                ariaLabel={`${SUBSCRIPTION_EVENT_LABELS[s.event_type_id] ?? s.event_type_id}を${s.is_subscribed ? 'オフ' : 'オン'}にする`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const LINE_LINK_TOAST: Record<string, { type: 'success' | 'error'; message: string }> = {
  success: { type: 'success', message: 'LINE連携が完了しました' },
  conflict: { type: 'error', message: 'このLINEアカウントは既に別の利用者に連携されています。心当たりがない場合は管理者にご相談ください' },
  error: { type: 'error', message: 'LINE連携に失敗しました。もう一度お試しください' },
};

/** v0.4 §7-6 (L-4)・追加要件⑲改訂: LINEアカウント自己申告連携。employee_id と紐付いているユーザーのみ
 *  対象 (パート層含め全社員がアカウントを持つ前提、D-9)。`GET /line/link/me` は本人の employee_id を
 *  解決するために新設したエンドポイント (`GET /employees` はisCompanyAdmin限定、`GET /auth/me`
 *  はemployee_idを含まないため、一般スタッフが自分のemployee_idを知る既存手段が無かった)。
 *
 *  ⚠️ 初回連携は「コードのコピペ」方式(D-840)を廃止し、LINE Login OAuthに置き換えた(D-845)。
 *  設計: docs/design/v04-line-account-linking-oauth-detail.md */
function LineLinkSection() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: me, isLoading } = useQuery<LineLinkMe>({
    queryKey: ['line-link-me'],
    queryFn: () => apiFetch<LineLinkMe>('/line/link/me'),
    retry: false,
  });
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [starting, setStarting] = useState(false);
  const lineLinkToastShown = useRef(false);

  // v0.4 追加要件⑲改訂 §5-1・D-862: OAuth完了後のリダイレクトをtoastで表示し、
  // 表示後はURLからクエリパラメータを除去する。
  // ⚠️ D-862 (2026-09-10): 旧D-861のCookie方式はWebKit(iOS Safari・iOS上の全ブラウザ)の
  // クロスサイトCookieブロックで機能しなかったため廃止。callbackは`?line_link_pending=<id>`で
  // 戻ってくるようになり、この認証済みSPAタブ自身が`POST /line/oauth/link/confirm`を呼んで
  // 「今ログインしている本人」であることを確定させてから連携を成立させる (旧`?line_link=success/conflict`
  // は廃止、`?line_link=error`のみ残る)。
  // ⚠️ 2巡目実装レビューMinor反映 (design-expert): React.StrictMode下ではeffectが開発時に2回実行されうる。
  // ref guardで実行を1回に限定し、開発中の「toastが2回出る」誤報告を避ける
  useEffect(() => {
    if (lineLinkToastShown.current) return;
    const pendingId = searchParams.get('line_link_pending');
    const errorResult = searchParams.get('line_link');
    if (!pendingId && !errorResult) return;
    lineLinkToastShown.current = true;

    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('line_link_pending');
      next.delete('line_link');
      setSearchParams(next, { replace: true });
    };

    if (pendingId) {
      apiFetch<{ status: 'linked' | 'conflict' }>('/line/oauth/link/confirm', {
        method: 'POST', body: JSON.stringify({ pending_id: pendingId }),
      }).then((res) => {
        const toastSpec = LINE_LINK_TOAST[res.status === 'linked' ? 'success' : 'conflict']!;
        toast[toastSpec.type](toastSpec.message);
        qc.invalidateQueries({ queryKey: ['line-link-me'] });
      }).catch((e) => {
        toast.error(e instanceof ApiClientError ? e.message : LINE_LINK_TOAST.error!.message);
      }).finally(clearParams);
      return;
    }

    const toastSpec = LINE_LINK_TOAST[errorResult!];
    if (toastSpec) toast[toastSpec.type](toastSpec.message);
    clearParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ処理する
  }, []);

  // v0.4 追加要件⑲ D-492: 連携の解除。⚠️ ⑲ で LINE は「通知の宛先」から
  // 「パスワードを再発行できる事実上の認証要素」へ格上げされたため、端末の紛失や
  // 機種変更の際に本人がその場で切れる手段が無いと、リセットし放題の状態が残り続ける。
  const unlinkMut = useMutation({
    mutationFn: () => apiFetch<{ linked: boolean }>(`/line/link/${me!.employee_id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmUnlink(false);
      qc.invalidateQueries({ queryKey: ['line-link-me'] });
      toast.success('LINE連携を解除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '連携の解除に失敗しました'),
  });

  const handleStart = async () => {
    setStarting(true);
    try {
      await startLineOauthLink();
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : 'LINE連携の開始に失敗しました');
      setStarting(false);
    }
  };

  if (!isLoading && !me?.employee_id) return null;

  return (
    <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <MessageCircle size={16} className="text-chrome-base" />
        <h2 className="text-h3">LINEと連携する</h2>
      </div>
      {isLoading ? (
        <div className="text-neutral-sub text-sm">読み込み中…</div>
      ) : me?.linked ? (
        <div className="space-y-2">
          <p className="text-xs text-neutral-sub">
            このアカウントはLINEと連携済みです
            {me.linked_at ? `（${new Date(me.linked_at).toLocaleString('ja-JP')} に連携）` : ''}。
            トーク画面で「パスワード」と送信すると、パスワード再設定のリンクを受け取れます。
          </p>
          {/* ⚠️ 平常の説明文と同じ見た目にすると埋没する。⑲ で LINE は「パスワードを再発行できる
              事実上の認証要素」に格上げされたため、注意喚起として一段上げる (赤は使わない、
              ui-guidelines §2.2「赤は1画面1箇所」)。 */}
          <p className="flex items-start gap-2 rounded border border-alert-border/40 bg-alert-bg p-2 text-xs text-alert-text">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
            <span>
              端末の紛失・機種変更・譲渡の際は、必ず連携を解除してください。連携が有効なままだと、
              そのLINEアカウントを操作できる人がパスワードを再設定できます。
            </span>
          </p>
          <button
            type="button"
            onClick={() => setConfirmUnlink(true)}
            disabled={unlinkMut.isPending}
            className="px-4 py-2 rounded border border-neutral-line text-neutral-text hover:bg-neutral-panel disabled:opacity-50 min-h-[44px]"
          >
            {unlinkMut.isPending ? '解除中…' : '連携を解除する'}
          </button>
          <ConfirmDialog
            open={confirmUnlink}
            title="LINE連携を解除しますか？"
            message="解除するとMiseSparkからの通知が届かなくなり、LINEからのパスワード再設定もできなくなります。再度連携するにはもう一度LINEでの連携操作が必要です。"
            confirmLabel="解除する"
            destructive
            loading={unlinkMut.isPending}
            onConfirm={() => unlinkMut.mutate()}
            onClose={() => setConfirmUnlink(false)}
          />
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={handleStart}
            disabled={starting}
            className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50 min-h-[44px]"
          >
            {starting ? 'LINEに移動中…' : 'LINEと連携する'}
          </button>
          {/* ⚠️ 1巡目レビューCritical反映 (design-expert): ボタン押下で即外部(LINE)へ遷移するため、
              事前に何が起きるかを明記する。bot_prompt=aggressive(D-843)による友だち追加の
              同意画面も想定外に見えないよう言及する。設計書§5-1通りボタン直下に配置する
              (2巡目実装レビューMajor反映、当初は説明文をボタンより上に置いていた) */}
          <p className="text-xs text-neutral-sub">
            タップするとLINEの画面に移動し、ログインと友だち追加(まだの場合)を行います。
            完了すると自動的にこの画面に戻ります。
          </p>
        </>
      )}
    </div>
  );
}

type ContactsMe = { employee_id: string | null };
type EmployeeContactChannel = {
  contact_id: string;
  channel_code: string;
  address: string;
  priority: number;
  verified_at: string | null;
  blocked_at: string | null;
};

const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** v0.4 F-6: メールアドレスの自己登録。`LineLinkSection` と同型 (`GET /contacts/me` で employee_id を
 *  解決してから、既に self scope 対応済みの `GET/POST/DELETE /employees/:id/contact-channels` を叩く)。
 *  employee_id が無いアカウントでは LineLinkSection 同様に非表示にする。 */
function EmailContactSection() {
  const qc = useQueryClient();
  const { data: me, isLoading: meLoading } = useQuery<ContactsMe>({
    queryKey: ['contacts-me'],
    queryFn: () => apiFetch<ContactsMe>('/contacts/me'),
    retry: false,
  });
  const employeeId = me?.employee_id ?? null;

  const { data: channelsData, isLoading: channelsLoading } = useQuery<{ contact_channels: EmployeeContactChannel[] }>({
    queryKey: ['employee-contact-channels', employeeId],
    queryFn: () => apiFetch<{ contact_channels: EmployeeContactChannel[] }>(`/employees/${employeeId}/contact-channels`),
    enabled: !!employeeId,
  });
  const emails = (channelsData?.contact_channels ?? []).filter((ch) => ch.channel_code === 'email');

  const [newAddress, setNewAddress] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeContactChannel | null>(null);

  const addMut = useMutation({
    mutationFn: () => apiFetch(`/employees/${employeeId}/contact-channels`, {
      method: 'POST',
      body: JSON.stringify({ channel_code: 'email', address: newAddress.trim() }),
    }),
    onSuccess: () => {
      setNewAddress('');
      toast.success('メールアドレスを登録しました');
      qc.invalidateQueries({ queryKey: ['employee-contact-channels', employeeId] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'メールアドレスの登録に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (contactId: string) => apiFetch(`/employees/${employeeId}/contact-channels/${contactId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteTarget(null);
      toast.success('メールアドレスを削除しました');
      qc.invalidateQueries({ queryKey: ['employee-contact-channels', employeeId] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  // 境界 (ユーザー入力) でのみバリデーションする (CLAUDE.md §6)。形式チェックはサーバー側の
  // zod (z.string().email()) と重複するが、送信前にフィードバックを返すためのUI側チェック。
  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    const trimmed = newAddress.trim();
    if (!EMAIL_FORMAT_RE.test(trimmed)) {
      setAddError('メールアドレスの形式が正しくありません');
      return;
    }
    addMut.mutate();
  };

  if (!meLoading && !employeeId) return null;

  return (
    <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <Mail size={16} className="text-chrome-base" />
        <h2 className="text-h3">通知用のメールアドレス</h2>
      </div>
      {/* v0.4 追加要件⑲ D-490: ここで登録するのは「通知の宛先」(employee_contact_channels) であり、
          ログイン用メールアドレス (CONTROL_DB.users.email) とは別物。パスワード再設定のリンクは
          ログイン用の方に送られるため、この区別を明示しないと「登録したのにリセットメールが来ない」
          という事故になる (ProfilePage から users.email は編集できない = 管理者管理)。 */}
      <p className="text-xs text-neutral-sub">
        MiseSparkからのお知らせを受け取るアドレスです。
        <strong className="font-bold">ログインやパスワード再設定に使うメールアドレスとは別</strong>で、
        こちらに登録してもパスワード再設定のリンクは届きません。ログイン用の変更は管理者へご依頼ください。
      </p>
      {(meLoading || channelsLoading) ? (
        <div className="text-neutral-sub text-sm">読み込み中…</div>
      ) : (
        <>
          {emails.length > 0 && (
            <ul className="divide-y divide-neutral-line">
              {emails.map((ch) => (
                <li key={ch.contact_id} className="flex items-center justify-between gap-2 py-2">
                  <span className="text-neutral-text truncate">{ch.address}</span>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(ch)}
                    aria-label={`${ch.address} を削除`}
                    className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded text-neutral-sub hover:text-danger-text hover:bg-danger-bg shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={handleAdd} className="flex items-start gap-2">
            <div className="flex-1">
              <label htmlFor="profile-email-address" className="sr-only">追加するメールアドレス</label>
              <input
                id="profile-email-address"
                type="email"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder="example@company.co.jp"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring min-h-[44px]"
              />
              {addError && <p className="text-xs text-danger-text mt-1">{addError}</p>}
            </div>
            <button
              type="submit"
              disabled={addMut.isPending || !newAddress.trim()}
              className="shrink-0 px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50 min-h-[44px]"
            >
              {addMut.isPending ? '登録中…' : '追加'}
            </button>
          </form>
        </>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="メールアドレスの削除"
        message={<>「{deleteTarget?.address}」を削除します。よろしいですか？</>}
        confirmLabel="削除する"
        destructive
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.contact_id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

export function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();

  // v0.3.x (AIトークン利用上限管理、F-12): 自分の当月AI利用状況。権限不要 (自分のデータは常に見られる)。
  // v1.0 Phase1-5最終監査 MEDIUM対応: ai_usage_management フラグがバックエンドでenforceされる
  // ようになったため (routes/ai-usage.ts)、フラグOFFのテナントでは常に FEATURE_DISABLED (403) が
  // 返る。パネル自体は `{aiUsage && (...)}` で自然に非表示になるが、無効テナントで毎回3回リトライ
  // する無駄を避けるため retry を無効化する
  const { data: aiUsage } = useQuery<AiUsageMe>({
    queryKey: ['ai-usage-me-summary'],
    queryFn: () => apiFetch<AiUsageMe>('/ai-usage/me/summary'),
    retry: false,
  });

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // v0.3 (docs/design/v03-user-employee-refinement.md §5-3): 本人氏名編集。
  // family/given/middle/display_nameはpermission不要で自己編集可能 (PATCH /users/:id の自己編集バイパス)。
  // 2026-08-05 実機確認で発覚: useStateの初期値はマウント時の1回だけ評価されるため、ページ直接
  // アクセス (リロード) 時のように /auth/me の非同期解決前にuserがnullの状態でマウントされると
  // 編集フォームが空欄のままになるバグがあった。編集開始時 (startEditingName) に都度userの最新値から
  // 値を詰め直すことで解消する (useEffectでの同期ではなく、編集トリガー時点のスナップショットにする)
  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [givenName, setGivenName] = useState('');
  const [middleName, setMiddleName] = useState('');

  const startEditingName = () => {
    setDisplayName(user?.display_name ?? user?.name ?? '');
    setFamilyName(user?.family_name ?? '');
    setGivenName(user?.given_name ?? '');
    setMiddleName(user?.middle_name ?? '');
    setEditingName(true);
  };

  const changePwMut = useMutation({
    mutationFn: () => apiFetch<{ changed: boolean }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: current, new_password: next }),
    }),
    onSuccess: () => {
      toast.success('パスワードを変更しました。再ログインしてください');
      // 少し待ってからログアウト → ログイン画面へ
      setTimeout(() => {
        clearTokens();
        setUser(null);
        navigate('/login', { replace: true });
      }, 800);
    },
    // onError はグローバル MutationCache（main.tsx）でトースト表示される
  });

  const updateNameMut = useMutation({
    mutationFn: () => apiFetch(`/users/${user!.user_id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        display_name: displayName.trim(),
        family_name: familyName.trim() || null,
        given_name: givenName.trim() || null,
        middle_name: middleName.trim() || null,
      }),
    }),
    onSuccess: () => {
      setUser({
        ...user!,
        name: displayName.trim(),
        display_name: displayName.trim(),
        family_name: familyName.trim() || null,
        given_name: givenName.trim() || null,
        middle_name: middleName.trim() || null,
      });
      setEditingName(false);
      toast.success('氏名を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '氏名の更新に失敗しました'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!isPasswordSetValid(next, confirmPw)) {
      setFormError(next.length < 8 ? '新しいパスワードは8文字以上で入力してください' : '新しいパスワードと確認用が一致しません');
      return;
    }
    if (current === next) {
      setFormError('現在のパスワードと同じものは指定できません');
      return;
    }
    changePwMut.mutate();
  };

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h1 className="text-h1">プロフィール</h1>
        <p className="text-sm text-neutral-sub mt-1">ログイン中ユーザーの情報とパスワードを管理します</p>
      </div>

      {/* ユーザー情報 */}
      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-2 text-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-chrome-base" />
            <h2 className="text-h3">アカウント情報</h2>
          </div>
          {!editingName && (
            <button type="button" onClick={startEditingName}
              className="flex items-center gap-1 text-xs text-chrome-base hover:underline">
              <UserCog size={14} /> 氏名を編集
            </button>
          )}
        </div>

        {editingName ? (
          <form
            onSubmit={(e) => { e.preventDefault(); if (displayName.trim()) updateNameMut.mutate(); }}
            className="space-y-3 pb-2"
          >
            <div>
              <label htmlFor="profile-display-name" className="block text-xs text-neutral-sub mb-1">表示名*</label>
              <input id="profile-display-name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                maxLength={100}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div className="grid grid-cols-[5fr_2fr_5fr] gap-2">
              <div>
                <label htmlFor="profile-family-name" className="block text-xs text-neutral-sub mb-1">姓 (Family)</label>
                <input id="profile-family-name" value={familyName} onChange={(e) => setFamilyName(e.target.value)} maxLength={50}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label htmlFor="profile-middle-name" className="block text-xs text-neutral-sub mb-1">ミドル (任意)</label>
                <input id="profile-middle-name" value={middleName} onChange={(e) => setMiddleName(e.target.value)} maxLength={50}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
              <div>
                <label htmlFor="profile-given-name" className="block text-xs text-neutral-sub mb-1">名 (Given)</label>
                <input id="profile-given-name" value={givenName} onChange={(e) => setGivenName(e.target.value)} maxLength={50}
                  className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              </div>
            </div>
            <p className="text-xs text-neutral-sub">役職名や組織名を表示名にしている場合は姓/名は空でも構いません</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditingName(false)}
                className="px-3 py-1.5 rounded border border-neutral-line hover:bg-neutral-panel text-sm">キャンセル</button>
              <button type="submit" disabled={updateNameMut.isPending || !displayName.trim()}
                className="px-3 py-1.5 rounded bg-chrome-base text-white hover:opacity-90 disabled:opacity-50 text-sm">
                {updateNameMut.isPending ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <div className="text-neutral-sub">氏名</div><div className="col-span-2">{user ? formatUserName(user) : '−'}</div>
            <div className="text-neutral-sub">ログインID</div><div className="col-span-2 font-mono">{user?.login_id || '−'}</div>
            <div className="text-neutral-sub">メールアドレス</div>
            <div className="col-span-2 tabular-nums">
              {user?.email
                ? user.email
                : <span className="text-neutral-sub italic">未設定</span>}
            </div>
            <div className="text-neutral-sub">ロール</div><div className="col-span-2">{user?.roles?.join(', ') || '−'}</div>
          </div>
        )}
        <p className="text-xs text-neutral-sub mt-2">ログインID／メール／ロールを変更したい場合は管理者にお問い合わせください</p>
      </div>

      {/* AI 利用状況 (v0.3.x F-12) */}
      {aiUsage && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-2 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-chrome-base" />
            <h2 className="text-h3">AI 利用状況 ({aiUsage.month})</h2>
          </div>
          {aiUsage.appliedLimitUsd != null ? (
            <>
              <div className="flex items-center justify-between text-xs text-neutral-sub">
                <span>適用中の上限: ${aiUsage.appliedLimitUsd.toFixed(2)}/月</span>
                <span>消費: ${aiUsage.costUsd.toFixed(2)}</span>
              </div>
              {(() => {
                const pct = aiUsage.appliedLimitUsd > 0 ? Math.min(100, Math.round((aiUsage.costUsd / aiUsage.appliedLimitUsd) * 100)) : 0;
                return (
                  <div className="w-full h-2 rounded-full bg-neutral-panel overflow-hidden" role="progressbar"
                    aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                    <div className={`h-full ${pct >= 100 ? 'bg-danger-border' : pct >= 80 ? 'bg-alert-border' : 'bg-brand-red'}`} style={{ width: `${pct}%` }} />
                  </div>
                );
              })()}
            </>
          ) : (
            <p className="text-xs text-neutral-sub">
              当月消費: ${aiUsage.costUsd.toFixed(2)} ({aiUsage.requestCount.toLocaleString()} 回) ・ 適用中の上限はありません
            </p>
          )}
        </div>
      )}

      {/* 通知の受け取り設定 (v0.4 §7) */}
      <NotificationSubscriptionsSection />

      {/* LINE連携 (v0.4 §7-6) */}
      <LineLinkSection />

      {/* メールアドレス登録 (v0.4 F-6) */}
      <EmailContactSection />

      {/* パスワード変更 */}
      <div className="border border-neutral-line rounded-lg bg-white p-4">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={16} className="text-chrome-base" />
          <h2 className="text-h3">パスワード変更</h2>
        </div>

        <div className="bg-neutral-panel rounded p-3 text-xs text-neutral-sub mb-4">
          <ul className="list-disc pl-5 space-y-0.5">
            <li>新しいパスワードは <strong>8文字以上</strong> で入力してください</li>
            <li>変更が完了すると <strong>自動的にログアウト</strong> され、ログイン画面に戻ります</li>
            <li>目のアイコンでパスワードの一時表示／非表示を切替できます</li>
          </ul>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label htmlFor="current" className="block text-sm text-neutral-sub mb-1">現在のパスワード</label>
            <div className="relative">
              <input
                id="current"
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                minLength={8}
                autoComplete="current-password"
                className="w-full pl-3 pr-10 py-2 rounded border border-neutral-line focus-ring font-mono"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                aria-label="パスワードを表示/非表示"
                aria-pressed={showCurrent}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-neutral-sub hover:text-text-main min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <PasswordSetInput
            idPrefix="profile"
            value={next}
            confirmValue={confirmPw}
            onChange={setNext}
            onConfirmChange={setConfirmPw}
            label="新しいパスワード（8文字以上）"
            confirmLabel="新しいパスワード（確認用）"
          />

          {formError && (
            <div className="p-2 rounded bg-warning-bg text-warning-text border border-warning-border text-sm">
              {formError}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={changePwMut.isPending || !current || !next || !confirmPw}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50"
            >
              {changePwMut.isPending ? '変更中…' : 'パスワードを変更する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
